import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { apiFetch } from '../apiClient';
import { useApp } from '../appContext';
import { ErrorBanner } from './Configuration';

// The dashboards can't be iframed: SharePoint Online sends X-Frame-Options: SAMEORIGIN on
// AllItems.aspx, so the browser refuses to render it on our origin. A true in-app embed needs
// apps/api to fetch the HTML via Graph (Sites.Read.All, admin consent) and serve it from our own
// origin -- not built, so this page is a launcher. Admins maintain the list (TicketDashboard table).

const BLANK = { account: '', url: '', author: '' };
const SMALL = { fontSize: 12, padding: '4px 10px' };

function TicketStatus() {
  const { currentUser } = useApp();
  const isAdmin = !!currentUser && currentUser.role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null); // { id?, account, url } while adding/editing
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      setRows(await apiFetch('/ticket-dashboards'));
    } catch (e) {
      setError(e.message || 'Could not load dashboards.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = JSON.stringify({ account: form.account, url: form.url, author: form.author || '' });
      if (form.id) await apiFetch(`/ticket-dashboards/${form.id}`, { method: 'PATCH', body });
      else await apiFetch('/ticket-dashboards', { method: 'POST', body });
      setForm(null);
      await load();
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove the ${d.account} dashboard link?`)) return;
    setError('');
    try {
      await apiFetch(`/ticket-dashboards/${d.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed.');
    }
  };

  const q = query.trim().toLowerCase();
  const shown = q ? rows.filter((d) => `${d.account} ${d.author || ''}`.toLowerCase().includes(q)) : rows;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">TS Ticket Dashboards</div>
        <div className="page-subtitle">
          Ticket dashboards, per customer. These open in SharePoint — you'll need access
          to the customer's site.
        </div>
      </div>

      <ErrorBanner error={error} />

      {isAdmin && !form && (
        <div style={{ marginBottom: 16 }}>
          <button className="btn btn-outline" onClick={() => setForm({ ...BLANK })}>
            <Plus size={16} strokeWidth={1.75} aria-hidden /> Add dashboard
          </button>
        </div>
      )}

      {form && (
        <form className="card" onSubmit={save}>
          <div className="card-title">{form.id ? 'Edit dashboard' : 'Add dashboard'}</div>
          <div className="form-group">
            <label>Customer</label>
            <input className="form-input" value={form.account} required
              onChange={(e) => setForm({ ...form, account: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Dashboard URL</label>
            <input className="form-input" value={form.url} required placeholder="https://"
              onChange={(e) => setForm({ ...form, url: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Author (who built it)</label>
            <input className="form-input" value={form.author || ''}
              onChange={(e) => setForm({ ...form, author: e.target.value })} />
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>{' '}
          <button className="btn btn-outline" type="button" onClick={() => setForm(null)}>Cancel</button>
        </form>
      )}

      {rows.length > 0 && (
        <input className="form-input" type="search" placeholder="Search by customer or author"
          aria-label="Search dashboards" value={query} style={{ marginBottom: 16, maxWidth: 360 }}
          onChange={(e) => setQuery(e.target.value)} />
      )}

      {!loading && rows.length === 0 && !error && (
        <div className="card"><p className="page-subtitle">No dashboards yet.{isAdmin ? '' : ' An admin can add them.'}</p></div>
      )}

      {rows.length > 0 && shown.length === 0 && (
        <div className="card"><p className="page-subtitle">No dashboards match “{query}”.</p></div>
      )}

      {shown.map((d) => (
        <div className="card" key={d.id}>
          <div className="card-title">{d.account}</div>
          <p className="page-subtitle" style={{ marginBottom: 16 }}>
            Open ticket counts, aging and status, published to the customer's external SharePoint site.
            {d.author && <><br />Author: {d.author}</>}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <a className="btn btn-primary" href={d.url} target="_blank" rel="noopener noreferrer">
              Open dashboard
              <ExternalLink size={16} strokeWidth={1.75} aria-hidden />
            </a>
            {isAdmin && (
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button className="btn btn-outline" style={SMALL} onClick={() => setForm({ id: d.id, account: d.account, url: d.url, author: d.author || '' })}>
                  <Pencil size={13} strokeWidth={1.75} aria-hidden /> Edit
                </button>
                <button className="btn btn-outline" style={SMALL} onClick={() => remove(d)}>
                  <Trash2 size={13} strokeWidth={1.75} aria-hidden /> Remove
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default TicketStatus;
